import { Theme } from '@material-ui/core';
import { CSSProperties } from '@material-ui/core/styles/withStyles';

// ---------------------------------------------------------------------------
// Shared style constants for Bulk Change Billing components
//
// Values are derived from the app theme (theme.tsx) to stay consistent:
//   - Font sizes use the typography scale (caption = 0.75rem)
//   - Grey tones use the defined palette keys (300, 500, 800)
//   - Dimensions use rem to match the theme's rem-based spacing
// ---------------------------------------------------------------------------

/** Standard small chip dimensions used across all bulk-change-billing tables and summaries */
export const CHIP_SMALL: CSSProperties = {
  height: '1.375rem', // ~22px at 16px base, consistent with rem-based theme
  fontSize: '0.75rem', // matches MUI caption / theme typography scale
};

/** Neutral gray chip for non-semantic status indicators (reservation lifecycle, summary counts) */
export function neutralChipStyle(theme: Theme): CSSProperties {
  return {
    ...CHIP_SMALL,
    backgroundColor: theme.palette.grey[300], // LightGray — defined in theme palette
    color: theme.palette.grey[500], // Gray — muted, avoids theme's dark text.secondary
  };
}

/** Prevent cell content from wrapping */
export const cellNoWrapStyle: CSSProperties = {
  whiteSpace: 'nowrap',
};

/** Truncate text with ellipsis; pair with a Tooltip for full-value access */
export const cellTruncateStyle: CSSProperties = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

/** Tabular number alignment for currency / numeric columns */
export const tabularNumsStyle: CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
  textAlign: 'right',
};

// ---------------------------------------------------------------------------
// Compact table style — page-local DataGrid overrides
// ---------------------------------------------------------------------------

/**
 * Compact, density-optimised DataGrid overrides for Bulk Change Billing tables.
 *
 * - Removes per-cell borders; adds single row-level border.
 * - Targets ~36–40px row height via tight padding + line-height.
 * - Compacts header to ~40px.
 * - Softens zebra to 2–3% grey (overrides base `AppColors.LightGray`).
 * - Adds subtle hover highlight for row scanning.
 * - Forces single-line cells (nowrap) to prevent row expansion.
 * - Constrains horizontal padding for tighter column rhythm.
 */
export function compactTableStyle(theme: Theme): CSSProperties {
  return {
    // --- Cells: compact padding, single-line, no per-cell border ---
    '& .MuiDataGrid-cell': {
      borderBottom: 'none',
      alignItems: 'center',
      paddingTop: '0.25rem', // 4px
      paddingBottom: '0.25rem',
      paddingLeft: '0.5rem', // 8px — tighter horizontal rhythm
      paddingRight: '0.5rem',
      lineHeight: '1.3 !important',
      minHeight: 'unset !important',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },

    // --- Rows: single border, soft zebra, hover ---
    '& .MuiDataGrid-row': {
      borderBottom: `1px solid ${theme.palette.divider}`,
      minHeight: 'unset !important',
      maxHeight: 'unset !important',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.02)',
      },
    },
    // Soft zebra — overrides the global LightGray (#DEDEDE)
    '& .MuiDataGrid-row:nth-child(odd)': {
      backgroundColor: 'rgba(0, 0, 0, 0.015)',
    },
    '& .MuiDataGrid-row:nth-child(odd):hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.035)',
    },

    // --- Header: compact height ---
    '& .MuiDataGrid-columnHeaderWrapper': {
      height: '2.5rem !important', // 40px
    },
    '& .MuiDataGrid-columnHeader': {
      paddingLeft: '0.5rem',
      paddingRight: '0.5rem',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontSize: '0.75rem',
      fontWeight: 600,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },

    // --- Checkbox column: tighter ---
    '& .MuiDataGrid-cellCheckbox, & .MuiDataGrid-columnHeaderCheckbox': {
      minWidth: '36px !important',
      maxWidth: '36px !important',
    },
  } as any;
}

// Keep legacy export as alias so existing dialog import still works
export const rowLevelBorderStyle = compactTableStyle;
